import { rsc as isReactServerComponentContext } from 'rsc-env';
import type { NormalizedRequestModel } from '@/services/models/Http';
import { InvalidStateError, NotImplementedError } from '@/utils/errors';
import { cache } from 'react';
import CurrentRequestService from '../CurrentRequestService';

/** Represents the server rendering context of a request. */
export interface IRenderContext {
  /** The request that is being processed. */
  request: NormalizedRequestModel;
  /** The preview value, if any, associated with the request and render. */
  preview: string | null;
}

/**
 * The `RenderContextService` fills a hole in our `CurrentRequestService`. It is used to store
 * and retrieve relevant information about the state of the current context. Currently
 * this is only useful for ServerComponents, if used in a client component it will throw
 * a `NotImplementedError`. Right now this error is expected in the `I18nService` and ignored.
 *
 * TODO: In the long run, after we have all routes on the App Router, we should reconsider
 * both this service and the `CurrentRequestService` and see what kind of refactor makes sense.
 */
export class RenderContextService {
  private _context: IRenderContext | null = null;

  /** @returns Whether there is a run context stored. */
  public get isContextSupported(): boolean {
    return !CurrentRequestService.inRequest && isReactServerComponentContext;
  }

  private getContext = cache(() => {
    if (!this.isContextSupported) {
      throw new NotImplementedError(
        'The RenderContextService cannot be used outside of a Server Component.'
      );
    }

    return this._context;
  });

  /**
   * @returns The cached run context.
   * @throws A {@link NotImplementedError} if the context is not supported. Use
   * {@link isContextSupported} to check for support.
   * @throws An {@link InvalidStateError} if the context has not been stored.
   */
  public get context(): IRenderContext {
    const context = this.getContext();

    if (!context) {
      throw new InvalidStateError(
        'Run context has not been stored. Do not retrieve it without storing it first.'
      );
    }

    return context;
  }

  /**
   * Stores the context in the service.
   * @param req - The request to store.
   * @throws {NotImplementedError} If the context is not supported.
   */
  public storeContext(req: NormalizedRequestModel): void {
    this._context = { request: req, preview: null };
    this.getContext(); // load the context into the cache
    this._context = null; // clear the reference to avoid memory leaks
  }
}

export default new RenderContextService();
